// ============================================
// Release of Information Section
// ECM ROI specific fields
// ============================================

import React from 'react';
import { useFormContext } from 'react-hook-form';
import { FormSubmission } from '../../types';
import FormField from '../ui/FormField';
import { useLanguage } from '../../i18n/LanguageContext';

const FIXED_PROVIDER_TEXT = 'California Support Services 420 Railroad Ave. Suite #103, Pittsburg, CA 94565';

const ROISection: React.FC = () => {
  const { register, watch, setValue } = useFormContext<FormSubmission>();
  const { t } = useLanguage();
  const expirationOption = watch('roi.expirationOption');
  const infoOther = watch('roi.informationToRelease.other');
  const purposeOther = watch('roi.purposeOfDisclosure.other');

  React.useEffect(() => {
    // Enforce fixed provider text in both From/To name fields and keep other fields empty.
    setValue('roi.providerFrom.name', FIXED_PROVIDER_TEXT, { shouldDirty: false });
    setValue('roi.providerTo.name', FIXED_PROVIDER_TEXT, { shouldDirty: false });
    setValue('roi.providerFrom.address', '', { shouldDirty: false });
    setValue('roi.providerFrom.phone', '', { shouldDirty: false });
    setValue('roi.providerFrom.fax', '', { shouldDirty: false });
    setValue('roi.providerTo.address', '', { shouldDirty: false });
    setValue('roi.providerTo.phone', '', { shouldDirty: false });
    setValue('roi.providerTo.fax', '', { shouldDirty: false });
  }, [setValue]);

  return (
    <div className="space-y-8">
      <p className="text-sm text-gray-600 bg-blue-50 p-4 rounded-lg">
        {t('roi.autoFillNotice')}
      </p>

      {/* Authorizing Organization */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('roi.authorizingOrgHeading')}</h3>
        <FormField label={t('roi.ecmProviderName')}>
          <input
            type="text"
            {...register('roi.ecmProviderName')}
            className="form-input w-full rounded-md border-gray-300"
            placeholder={t('roi.ecmProviderNamePlaceholder')}
          />
        </FormField>
      </div>

      {/* Information to Release */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('roi.infoToReleaseHeading')}
          
        </h3>
        <p className="text-sm text-gray-500 mb-4">{t('roi.checkAllApply')}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input
              type="checkbox"
              {...register('roi.informationToRelease.carePlans')}
              className="form-checkbox h-5 w-5 text-blue-600 rounded"
            />
            <span className="text-gray-700">{t('roi.carePlans')}</span>
          </label>

          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input
              type="checkbox"
              {...register('roi.informationToRelease.medicalHistory')}
              className="form-checkbox h-5 w-5 text-blue-600 rounded"
            />
            <span className="text-gray-700">{t('roi.medicalHistory')}</span>
          </label>

          <label className="flex items-center space-x-3 p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 cursor-pointer border border-yellow-200">
            <input
              type="checkbox"
              {...register('roi.informationToRelease.mentalHealth')}
              className="form-checkbox h-5 w-5 text-yellow-600 rounded"
            />
            <span className="text-gray-700">{t('roi.mentalHealth')}</span>
          </label>

          <label className="flex items-center space-x-3 p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 cursor-pointer border border-yellow-200">
            <input
              type="checkbox"
              {...register('roi.informationToRelease.substanceUse')}
              className="form-checkbox h-5 w-5 text-yellow-600 rounded"
            />
            <span className="text-gray-700">{t('roi.substanceUse')}</span>
          </label>

          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input
              type="checkbox"
              {...register('roi.informationToRelease.medications')}
              className="form-checkbox h-5 w-5 text-blue-600 rounded"
            />
            <span className="text-gray-700">{t('roi.medications')}</span>
          </label>

          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input
              type="checkbox"
              {...register('roi.informationToRelease.hospitalizationER')}
              className="form-checkbox h-5 w-5 text-blue-600 rounded"
            />
            <span className="text-gray-700">{t('roi.hospitalizationER')}</span>
          </label>

          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input
              type="checkbox"
              {...register('roi.informationToRelease.socialServices')}
              className="form-checkbox h-5 w-5 text-blue-600 rounded"
            />
            <span className="text-gray-700">{t('roi.socialServices')}</span>
          </label>

          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input
              type="checkbox"
              {...register('roi.informationToRelease.billing')}
              className="form-checkbox h-5 w-5 text-blue-600 rounded"
            />
            <span className="text-gray-700">{t('roi.billing')}</span>
          </label>

          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input
              type="checkbox"
              {...register('roi.informationToRelease.other')}
              className="form-checkbox h-5 w-5 text-blue-600 rounded"
            />
            <span className="text-gray-700">{t('roi.other')}</span>
          </label>
        </div>

        {infoOther && (
          <div className="mt-4">
            <FormField label={t('roi.specifyOtherInfo')}>
              <input
                type="text"
                {...register('roi.informationToRelease.otherDescription')}
                className="form-input w-full rounded-md border-gray-300"
                placeholder={t('roi.specifyOtherInfoPlaceholder')}
              />
            </FormField>
          </div>
        )}
      </div>

      {/* Parties Authorized */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Provider From */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('roi.fromHeading')}</h3>

          <div className="space-y-4">
            <FormField label={t('roi.providerOrgName')}>
              <input
                type="text"
                {...register('roi.providerFrom.name')}
                readOnly
                className="form-input w-full rounded-md border-gray-300 bg-gray-100"
                placeholder={t('roi.providerOrgPlaceholder')}
              />
            </FormField>
          </div>
        </div>

        {/* Provider To */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('roi.toHeading')}</h3>

          <div className="space-y-4">
            <FormField label={t('roi.providerOrgName')}>
              <input
                type="text"
                {...register('roi.providerTo.name')}
                readOnly
                className="form-input w-full rounded-md border-gray-300 bg-gray-100"
                placeholder={t('roi.providerOrgPlaceholder')}
              />
            </FormField>
          </div>
        </div>
      </div>

      {/* Purpose of Disclosure */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('roi.purposeHeading')}</h3>
        <p className="text-sm text-gray-500 mb-4">{t('roi.checkAllApply')}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input
              type="checkbox"
              {...register('roi.purposeOfDisclosure.careCoordination')}
              className="form-checkbox h-5 w-5 text-blue-600 rounded"
            />
            <span className="text-gray-700">{t('roi.careCoordination')}</span>
          </label>

          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input
              type="checkbox"
              {...register('roi.purposeOfDisclosure.ecmServiceDelivery')}
              className="form-checkbox h-5 w-5 text-blue-600 rounded"
            />
            <span className="text-gray-700">{t('roi.ecmServiceDelivery')}</span>
          </label>

          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input
              type="checkbox"
              {...register('roi.purposeOfDisclosure.carePlanning')}
              className="form-checkbox h-5 w-5 text-blue-600 rounded"
            />
            <span className="text-gray-700">{t('roi.carePlanning')}</span>
          </label>

          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input
              type="checkbox"
              {...register('roi.purposeOfDisclosure.continuityOfCare')}
              className="form-checkbox h-5 w-5 text-blue-600 rounded"
            />
            <span className="text-gray-700">{t('roi.continuityOfCare')}</span>
          </label>

          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input
              type="checkbox"
              {...register('roi.purposeOfDisclosure.eligibility')}
              className="form-checkbox h-5 w-5 text-blue-600 rounded"
            />
            <span className="text-gray-700">{t('roi.eligibility')}</span>
          </label>

          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input
              type="checkbox"
              {...register('roi.purposeOfDisclosure.other')}
              className="form-checkbox h-5 w-5 text-blue-600 rounded"
            />
            <span className="text-gray-700">{t('roi.other')}</span>
          </label>
        </div>

        {purposeOther && (
          <div className="mt-4">
            <FormField label={t('roi.specifyOtherPurpose')}>
              <input
                type="text"
                {...register('roi.purposeOfDisclosure.otherDescription')}
                className="form-input w-full rounded-md border-gray-300"
                placeholder={t('roi.specifyOtherPurposePlaceholder')}
              />
            </FormField>
          </div>
        )}
      </div>

      {/* Expiration */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('roi.expirationHeading')}
          
        </h3>
        <p className="text-sm text-gray-500 mb-4">{t('roi.selectOne')}</p>

        <div className="space-y-3">
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input
              type="radio"
              {...register('roi.expirationOption')}
              value="one_year"
              className="form-radio h-5 w-5 text-blue-600"
            />
            <span className="text-gray-700">{t('roi.expOneYear')}</span>
          </label>

          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input
              type="radio"
              {...register('roi.expirationOption')}
              value="termination"
              className="form-radio h-5 w-5 text-blue-600"
            />
            <span className="text-gray-700">{t('roi.expTermination')}</span>
          </label>

          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input
              type="radio"
              {...register('roi.expirationOption')}
              value="specific_date"
              className="form-radio h-5 w-5 text-blue-600"
            />
            <span className="text-gray-700">{t('roi.expSpecificDate')}</span>
          </label>
        </div>

        {expirationOption === 'specific_date' && (
          <div className="mt-4 max-w-xs">
            <FormField label={t('roi.expirationDate')}>
              <input
                type="date"
                min="1900-01-01"
                {...register('roi.specificExpirationDate')}
                className="form-input w-full rounded-md border-gray-300"
              />
            </FormField>
          </div>
        )}
      </div>

      {/* Special Consent Initials */}
      <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t('roi.specialConsentHeading')}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {t('roi.specialConsentNote')}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField label={t('roi.mentalHealthInfo')}>
            <input
              type="text"
              {...register('roi.mentalHealthInitial')}
              className="form-input w-full rounded-md border-gray-300 text-center uppercase"
              placeholder={t('roi.initialPlaceholder')}
              maxLength={5}
            />
          </FormField>

          <FormField label={t('roi.sudInfo')}>
            <input
              type="text"
              {...register('roi.sudInitial')}
              className="form-input w-full rounded-md border-gray-300 text-center uppercase"
              placeholder={t('roi.initialPlaceholder')}
              maxLength={5}
            />
          </FormField>

          <FormField label={t('roi.hivInfo')}>
            <input
              type="text"
              {...register('roi.hivInitial')}
              className="form-input w-full rounded-md border-gray-300 text-center uppercase"
              placeholder={t('roi.initialPlaceholder')}
              maxLength={5}
            />
          </FormField>
        </div>
      </div>

      {/* Witness Section (Optional) */}
      <div className="border-t pt-6">
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('roi.witnessHeading')}</h3>
          <p className="text-sm text-gray-600 mb-4">
            {t('roi.witnessNote')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label={t('roi.witnessName')}>
              <input
                type="text"
                {...register('roi.witnessName')}
                className="form-input w-full rounded-md border-gray-300"
                placeholder={t('roi.witnessNamePlaceholder')}
              />
            </FormField>

            <FormField label={t('roi.witnessDate')}>
              <input
                type="date"
                min="1900-01-01"
                {...register('roi.witnessDate')}
                className="form-input w-full rounded-md border-gray-300"
              />
            </FormField>
          </div>

          <div className="mt-4 text-sm text-gray-500">
            <p>{t('roi.witnessSignatureNote')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROISection;
